import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  FormControl,
  FormLabel,
  Box,
  Badge,
  Divider,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import axios from "axios";
import { BASE_URL } from "../services/api";

const OfferApprovalModal = ({
  isOpen,
  onClose,
  approval,
  canTakeAction,
  onApprovalComplete,
}) => {
  const [comments, setComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [sendBackReason, setSendBackReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' | 'reject' | 'send_back'
  const toast = useToast();

  const offerEntityId = approval?.offerId?._id || approval?.offerId;
  const candidateName =
    approval?.offerSnapshot?.candidateName ||
    approval?.offerId?.offerId?.candidateId?.fullName ||
    "N/A";
  const positionTitle =
    approval?.offerSnapshot?.position ||
    approval?.offerId?.designation ||
    "N/A";
  const ctc =
    approval?.offerSnapshot?.ctc ||
    approval?.offerSnapshot?.salary ||
    approval?.offerId?.ctc ||
    null;

  useEffect(() => {
    if (!isOpen) {
      setComments("");
      setRejectionReason("");
      setSendBackReason("");
      setActionType(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleApprove = async () => {
    if (!offerEntityId) {
      toast({
        title: "Invalid offer",
        description: "Unable to identify this offer for approval.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionType("approve");
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/offers/${offerEntityId}/approve`,
        { comments },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      toast({
        title: "Offer Approved",
        description:
          response.data.message || "Offer has been approved successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onApprovalComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Approval Failed",
        description: error.response?.data?.message || "Failed to approve offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setActionType(null);
    }
  };

  const handleReject = async () => {
    if (!offerEntityId) {
      toast({
        title: "Invalid offer",
        description: "Unable to identify this offer for rejection.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!rejectionReason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a reason for rejection",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionType("reject");
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/offers/${offerEntityId}/reject`,
        { rejectionReason, comments },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      toast({
        title: "Offer Rejected",
        description: response.data.message || "Offer has been rejected",
        status: "info",
        duration: 5000,
        isClosable: true,
      });

      onApprovalComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Rejection Failed",
        description: error.response?.data?.message || "Failed to reject offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setActionType(null);
    }
  };

  const handleSendBack = async () => {
    if (!offerEntityId) {
      toast({
        title: "Invalid offer",
        description: "Unable to identify this offer for send back action.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!sendBackReason.trim()) {
      toast({
        title: "Send Back Reason Required",
        description: "Please provide a reason to send this offer back.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionType("send_back");
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/offers/${offerEntityId}/send-back`,
        { sendBackReason, comments },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      toast({
        title: "Offer Sent Back",
        description:
          response.data.message || "Offer has been sent back for revision",
        status: "info",
        duration: 5000,
        isClosable: true,
      });

      onApprovalComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Send Back Failed",
        description:
          error.response?.data?.message ||
          "Failed to send back this offer for revision",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setActionType(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Offer Approval - {approval?.offerSnapshot?.offerId || "N/A"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Offer Information */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" mb={2}>
                Offer Details
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text color="gray.600">Candidate:</Text>
                  <Text fontWeight="semibold">{candidateName}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Position:</Text>
                  <Text fontWeight="semibold">{positionTitle}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">CTC:</Text>
                  <Text fontWeight="semibold" color="green.600">
                    {ctc ? `₹${ctc.toLocaleString()}` : "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Current Status:</Text>
                  <Badge
                    colorScheme={
                      approval?.approvalStatus === "pending" ? "yellow" : "blue"
                    }
                  >
                    {approval?.approvalStatus || "N/A"}
                  </Badge>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Approval Workflow Info */}
            {approval?._id && (
              <Box>
                <Text fontWeight="bold" fontSize="lg" mb={2}>
                  Approval Workflow
                </Text>
                <HStack justify="space-between">
                  <Text color="gray.600">Workflow Type:</Text>
                  <Badge colorScheme="purple">
                    {approval.workflowType || "sequential"}
                  </Badge>
                </HStack>
                <HStack justify="space-between" mt={2}>
                  <Text color="gray.600">Current Stage:</Text>
                  <Text fontWeight="semibold">
                    Stage {(approval.currentStage || 0) + 1}
                  </Text>
                </HStack>
              </Box>
            )}

            <Divider />

            {/* Comments Section */}
            <FormControl>
              <FormLabel>Comments (Optional)</FormLabel>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments or notes..."
                rows={3}
              />
            </FormControl>

            {/* Rejection Reason Section */}
            <FormControl>
              <FormLabel>
                Rejection Reason{" "}
                {actionType === "reject" && (
                  <Text as="span" color="red.500">
                    *
                  </Text>
                )}
              </FormLabel>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Required if rejecting the offer..."
                rows={3}
                borderColor={
                  actionType === "reject" && !rejectionReason
                    ? "red.300"
                    : "gray.200"
                }
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                Send Back Reason{" "}
                {actionType === "send_back" && (
                  <Text as="span" color="red.500">
                    *
                  </Text>
                )}
              </FormLabel>
              <Textarea
                value={sendBackReason}
                onChange={(e) => setSendBackReason(e.target.value)}
                placeholder="Required if sending back for revision..."
                rows={3}
                borderColor={
                  actionType === "send_back" && !sendBackReason
                    ? "red.300"
                    : "gray.200"
                }
              />
            </FormControl>

            {!canTakeAction && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  This approval is not currently pending for you. You can view
                  details, but only the active approver can take action.
                </Text>
              </Alert>
            )}

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontSize="sm">
                  Your decision will be recorded with timestamp and user
                  details.
                  {approval?.workflowType === "sequential" &&
                    " The offer will move to the next approval stage upon approval."}
                </Text>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleReject}
              isLoading={isSubmitting && actionType === "reject"}
              isDisabled={isSubmitting || !canTakeAction}
            >
              Reject
            </Button>
            <Button
              colorScheme="orange"
              variant="outline"
              onClick={handleSendBack}
              isLoading={isSubmitting && actionType === "send_back"}
              isDisabled={isSubmitting || !canTakeAction}
            >
              Send Back
            </Button>
            <Button
              colorScheme="green"
              onClick={handleApprove}
              isLoading={isSubmitting && actionType === "approve"}
              isDisabled={isSubmitting || !canTakeAction}
            >
              Approve
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OfferApprovalModal;
